import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Briefcase, Edit, Building2 } from 'lucide-react';
import { directorsAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { toast } from 'sonner';
import logger from '@/lib/logger';
import DirectorFormDialog from './components/DirectorFormDialog';

export default function DirectorDetailPage() {
  const { directorId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [director, setDirector] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await directorsAPI.get(directorId);
      setDirector(res.data);
    } catch (err) {
      logger.error('Failed to load director', err);
      toast.error('Director not found');
      navigate('/dashboard/directors');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [directorId]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>;
  if (!director) return null;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/directors')} data-testid="back-to-directors">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Briefcase className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-semibold">{director.name}</h1>
          <p className="text-xs text-muted-foreground font-mono">DIN: {director.din}</p>
        </div>
        {isAdmin && (
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="edit-director-btn">
            <Edit className="h-4 w-4 mr-1.5" /> Edit
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-5 space-y-4">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Profile</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <Field label="Type" value={<Badge variant="secondary" className="text-[10px]">{director.type || 'Director'}</Badge>} />
            <Field label="Status" value={director.active === false ? <Badge variant="outline" className="text-[10px] text-muted-foreground">Inactive</Badge> : <Badge className="text-[10px] bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>} />
            <Field label="DIN" value={<span className="font-mono">{director.din}</span>} />
            <Field label="PAN" value={director.pan ? <span className="font-mono">{director.pan}</span> : '—'} />
            <Field label="Email" value={director.email || '—'} />
            <Field label="Phone" value={director.phone || '—'} />
            <Field label="Date of Birth" value={director.date_of_birth || '—'} />
          </div>
          {director.notes && (
            <div className="pt-2 border-t">
              <p className="text-xs text-muted-foreground mb-1">Notes</p>
              <p className="text-sm whitespace-pre-wrap">{director.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Linked Companies / LLPs</h2>
            <Badge variant="outline" className="text-xs">{director.clients?.length || 0}</Badge>
          </div>
          {(!director.clients || director.clients.length === 0) ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Not yet linked to any company. Open a client and add this director from the "Directors / Partners" section.
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40%]">Company</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Appointed</TableHead>
                    <TableHead>Cessation</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {director.clients.map(c => (
                    <TableRow key={c.client_id} className="cursor-pointer hover:bg-muted/30" onClick={() => navigate('/dashboard/clients')} data-testid={`director-client-row-${c.client_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="font-medium text-sm">{c.company_name || c.client_name}</span>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="secondary" className="text-[10px]">{c.role}</Badge></TableCell>
                      <TableCell className="text-xs">{c.date_of_appointment || '—'}</TableCell>
                      <TableCell className="text-xs">{c.date_of_cessation ? <span className="text-red-500">{c.date_of_cessation}</span> : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <DirectorFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        director={director}
        onSaved={() => { setEditOpen(false); load(); }}
      />
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">{label}</p>
    <div className="text-sm">{value}</div>
  </div>
);
